import axios from "axios";
import { openAIKey } from "../config/api.config";

// Interface for channel data to be used in prompt generation
interface ChannelData {
  name: string;
  description?: string;
  subscriberCount?: string | number;
  viewCount?: string | number;
  videoCount?: string | number;
  genre?: string;
  type: "youtube" | "spotify" | "blog";
  videoUrl?: string; // YouTube video URL for promotion
  videoThumbnail?: string; // Thumbnail URL for the video
}

/**
 * Generate an outreach message using OpenAI
 * @param channelData Data about the channel/playlist to use in the prompt
 * @returns Generated outreach message
 */
export const generateOutreachMessage = async (
  channelData: ChannelData
): Promise<string> => {
  try {
    // Create a prompt based on the channel data
    const prompt = createPrompt(channelData);
    console.log("Generated OpenAI prompt:", prompt);

    // Make request to OpenAI API
    const response = await axios.post(
      "https://api.openai.com/v1/chat/completions",
      {
        model: "gpt-4o",
        messages: [
          {
            role: "system",
            content:
              "You are a professional music industry outreach specialist representing Distro Nation, a company with over a decade of experience empowering artists and labels to get the most out of their music online. Your task is to write concise, effective outreach messages that follow a specific structure and maintain a business-minded, no-nonsense approach.",
          },
          {
            role: "user",
            content: prompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 500,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${openAIKey}`,
        },
      }
    );

    // Extract the generated message
    const content = response.data.choices[0].message.content.trim();

    // If this is a YouTube video with a thumbnail, inject the thumbnail after the video URL
    const isYouTubeVideo =
      channelData.videoUrl &&
      (channelData.videoUrl.includes("youtube.com") ||
        channelData.videoUrl.includes("youtu.be"));
    let processedContent = content;

    if (isYouTubeVideo && channelData.videoThumbnail && channelData.videoUrl) {
      const thumbnailHtml = `<img src="${channelData.videoThumbnail}" alt="Video thumbnail" style="border-radius: 8px; margin: 10px 0; border: 0;">`;

      // Find the video URL in the content and add the thumbnail after it
      const videoUrlPattern = new RegExp(
        channelData.videoUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g"
      );

      if (content.includes(channelData.videoUrl)) {
        processedContent = processedContent.replace(
          videoUrlPattern,
          `${channelData.videoUrl}\n\n${thumbnailHtml}`
        );
      }
    }

    // Convert line breaks to HTML format for proper display in the rich text editor
    const formattedContent = processedContent
      .split("\n\n\n") // Split by tripple line breaks (paragraphs)
      .map((paragraph: string) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`) // Replace single line breaks with <br>
      .join("");

    return formattedContent;
  } catch (error) {
    console.error("Error generating outreach message:", error);
    throw new Error(
      "Failed to generate outreach message. Please try again later."
    );
  }
};

/**
 * DEPRECIATED Create a prompt for OpenAI based on channel data
 */
const createPrompt = (channelData: ChannelData): string => {
  const {
    name,
    description,
    subscriberCount,
    viewCount,
    videoCount,
    genre,
    type,
    videoUrl,
    videoThumbnail,
  } = channelData;

  let prompt = `Write a professional outreach message to ${name}, `;

  // Check if videoUrl is a YouTube URL
  const isYouTubeVideo =
    videoUrl &&
    (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be"));

  // Add type-specific details
  switch (type) {
    case "youtube":
      prompt += `a YouTube channel with ${
        subscriberCount || "many"
      } subscribers`;
      if (videoCount) prompt += ` and ${videoCount} videos`;
      if (viewCount) prompt += ` that has accumulated ${viewCount} views`;

      // Add video promotion details only if it's a YouTube video
      if (isYouTubeVideo) {
        prompt += `. We are promoting a specific YouTube video: ${videoUrl}`;
        if (videoThumbnail) {
          prompt += ` (thumbnail available at: ${videoThumbnail})`;
        }
      }
      break;
    case "spotify":
      prompt += `a Spotify playlist`;
      if (subscriberCount) prompt += ` with ${subscriberCount} followers`;
      break;
    case "blog":
      prompt += `a music blog`;
      break;
  }

  // Add genre if available
  if (genre) {
    prompt += ` in the ${genre} genre`;
  }

  // Add description if available
  if (description) {
    prompt += `. Here's their description: "${description}"`;
  }

  // Complete the prompt with instructions
  if (isYouTubeVideo) {
    prompt += `.\n\nWrite a SHORT, direct outreach message following this format:

"Hi [Name],

I've been checking out your content and thought you might be interested in reacting to this video:

${videoUrl}

Would you like to react to it?

Best,
[Your Name]
Distro Nation
[Contact Info]"

INSTRUCTIONS:
- Keep it VERY short and casual
- Use the exact format above
- Include the video link exactly as shown
- Don't add extra explanations or corporate language
- Make it sound like a quick, friendly message`;
  } else {
    prompt += `.\n\nThe message MUST follow this exact structure and formatting:

1. Greeting and Introduction - Start with "Hi [Name]," and a brief greeting on a separate line

2. Brief Introduction of Distro Nation - In a separate paragraph, introduce yourself and mention Distro Nation's decade+ experience empowering artists and labels

3. Purpose of Outreach and Benefits of Collaboration - In another paragraph, explain why you're reaching out and the mutual benefits of collaboration

4. Clear Call to Action - In a separate paragraph, include a specific request or next step

5. Closing with Appreciation - End with a professional closing followed by a signature block in this format:
   [Your Full Name]
   [Your Position]
   Distro Nation
   [Your Contact Information]

IMPORTANT: 
- Do NOT include a subject line - generate ONLY the body of the email
- Use proper paragraph breaks and spacing between sections
- The message should be concise, professional, and business-minded
- Don't use excessive flattery or unnecessary details
- Keep it direct and focused on mutual benefit`;
  }

  prompt += `
- The message should be ready to send as-is, with placeholders only in the signature block`;

  return prompt;
};
